import { t } from '../i18n';
import type {
  Asset,
  PatternAssetId,
  RegionEntry,
  RegionStack,
  RegionWarning,
  StampMaskAssetId,
  Store,
} from '../state';

// ---------------------------------------------------------------------------
// relink-dialog.ts - Item 6 (Relink UI)
//
// Opens a modal asset picker scoped to the asset kind referenced by a
// RegionWarning. Selecting an asset updates the region entry's override
// to point at the new refId. Supports keyboard (Esc to close, Tab-trap,
// Enter/Space to select) and empty-library state.
//
// Public API:
//   showRelinkDialog({ parent, store, warning }) → void
//
// The dialog appends itself to `parent` on open and removes itself on close.
// Only one dialog is rendered at a time (the badge click guard prevents
// stacking; extra calls are safe due to the backdrop-click/Esc cleanup).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Draw an ImageData bitmap into a freshly-created <canvas> element. */
function makeThumb(data: ImageData, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.putImageData(data, 0, 0);
  }
  return canvas;
}

/**
 * Minimal focus-trap: collect all keyboard-focusable descendants of
 * `container` and cycle Tab / Shift+Tab within them.
 */
function focusTrap(container: HTMLElement, event: KeyboardEvent): void {
  if (event.key !== 'Tab') return;

  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null); // exclude hidden elements

  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey) {
    if (document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    }
  } else {
    if (document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }
}

// ---------------------------------------------------------------------------
// Pure patch-construction helpers (exported for unit testing)
// ---------------------------------------------------------------------------

/**
 * Build a new regionStacks array with the matching stack's refId updated.
 * Returns the next stacks array. If the stack or its override is not found,
 * or the asset kind does not match the snapshot family, the stack is returned
 * unchanged.
 *
 * Pure function - no DOM, no side effects.
 */
export function computeRelinkPatchForStack(
  stacks: RegionStack[],
  warning: Pick<RegionWarning, 'ownerUuid' | 'assetKind'>,
  newAsset: Pick<Asset, 'id'>,
): RegionStack[] {
  return stacks.map((stack) => {
    if (stack.uuid !== warning.ownerUuid) return stack;
    if (stack.override === null) return stack;

    const snap = stack.override;
    if (warning.assetKind === 'pattern' && snap.family === 'B') {
      return {
        ...stack,
        override: { ...snap, patternRefId: newAsset.id as PatternAssetId },
      };
    }
    if (
      warning.assetKind === 'stampMask' &&
      (snap.family === 'A' ||
        snap.family === 'C' ||
        (snap.family === 'B' && snap.patternHalftoneStampType === 'custom'))
    ) {
      return {
        ...stack,
        override: { ...snap, stampMaskRefId: newAsset.id as StampMaskAssetId },
      };
    }
    return stack;
  });
}

/**
 * Build a new regionEntries array with the matching entry's refId updated.
 * Returns the next entries array. If the entry or its override is not found,
 * or the asset kind does not match the snapshot family, the entry is returned
 * unchanged.
 *
 * Pure function - no DOM, no side effects.
 */
export function computeRelinkPatchForRegion(
  entries: RegionEntry[],
  warning: Pick<RegionWarning, 'ownerUuid' | 'assetKind'>,
  newAsset: Pick<Asset, 'id'>,
): RegionEntry[] {
  return entries.map((entry) => {
    if (entry.uuid !== warning.ownerUuid) return entry;
    if (entry.override === null) return entry;

    const snap = entry.override;
    if (warning.assetKind === 'pattern' && snap.family === 'B') {
      return {
        ...entry,
        override: { ...snap, patternRefId: newAsset.id as PatternAssetId },
      };
    }
    if (
      warning.assetKind === 'stampMask' &&
      (snap.family === 'A' ||
        snap.family === 'C' ||
        (snap.family === 'B' && snap.patternHalftoneStampType === 'custom'))
    ) {
      return {
        ...entry,
        override: { ...snap, stampMaskRefId: newAsset.id as StampMaskAssetId },
      };
    }
    return entry;
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type RelinkDialogOpts = {
  parent: HTMLElement;
  store: Store;
  warning: RegionWarning;
};

/**
 * Render a modal relink dialog for the given RegionWarning.
 *
 * The dialog filters store.get().assetLibrary by warning.assetKind and lets
 * the user select a replacement asset. On selection it patches the matching
 * region entry's override refId and closes. Cancel (button / backdrop / Esc)
 * closes without changes.
 */
export function showRelinkDialog({ parent, store, warning }: RelinkDialogOpts): void {
  // ---------------------------------------------------------------------------
  // Build DOM skeleton
  // ---------------------------------------------------------------------------

  const backdrop = document.createElement('div');
  backdrop.className = 'relink-backdrop';
  backdrop.setAttribute('role', 'presentation');

  const panel = document.createElement('div');
  panel.className = 'relink-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', 'relink-title');
  // Panels receive focus so the focus-trap works without relying on a child.
  panel.tabIndex = -1;

  backdrop.appendChild(panel);

  // --- Header ---
  const header = document.createElement('div');
  header.className = 'relink-header';

  const title = document.createElement('h2');
  title.id = 'relink-title';
  title.className = 'relink-title';
  title.textContent = t('dialog.relink.title');
  header.appendChild(title);

  panel.appendChild(header);

  // ---------------------------------------------------------------------------
  // Asset list
  // ---------------------------------------------------------------------------

  const state = store.get();
  const { assetLibrary, assetData } = state;
  const filtered = assetLibrary.filter((a) => a.kind === warning.assetKind);

  const body = document.createElement('div');
  body.className = 'relink-body';

  if (filtered.length === 0) {
    // Empty-library state.
    const emptyMsg = document.createElement('p');
    emptyMsg.className = 'relink-empty';
    const emptyKey =
      warning.assetKind === 'pattern'
        ? 'dialog.relink.empty.pattern'
        : 'dialog.relink.empty.stampMask';
    emptyMsg.textContent = t(emptyKey);
    body.appendChild(emptyMsg);
  } else {
    const list = document.createElement('ul');
    list.className = 'relink-asset-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', t('dialog.relink.title'));

    for (const asset of filtered) {
      const isCurrent = asset.id === warning.refId;
      const itemData = assetData.get(asset.id);

      const item = document.createElement('li');
      item.className = 'relink-asset-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(isCurrent));
      if (isCurrent) item.classList.add('current');
      item.tabIndex = 0;

      const nameText = asset.id.replace(/^[^:]+:/, '').slice(0, 8);
      item.setAttribute('aria-label', t('aria.relink.assetItem', { name: nameText }));

      // Thumbnail
      if (itemData) {
        const thumbCanvas = makeThumb(itemData, 40);
        thumbCanvas.className = 'relink-asset-thumb';
        item.appendChild(thumbCanvas);
      } else {
        // Asset metadata present but binary data missing - render placeholder.
        const placeholder = document.createElement('div');
        placeholder.className = 'relink-asset-thumb relink-asset-thumb--missing';
        placeholder.setAttribute('aria-hidden', 'true');
        item.appendChild(placeholder);
      }

      // Name badge
      const nameBadge = document.createElement('span');
      nameBadge.className = 'relink-asset-name';
      nameBadge.textContent = `${asset.kind}:${asset.contentHash.slice(0, 8)}`;
      if (isCurrent) {
        const currentTag = document.createElement('span');
        currentTag.className = 'relink-asset-current-tag';
        currentTag.textContent = '✓';
        nameBadge.appendChild(currentTag);
      }
      item.appendChild(nameBadge);

      // Click handler - commit relink (delegates to pure helpers for testability)
      const commitRelink = (): void => {
        const freshState = store.get();

        if (warning.ownerType === 'stack') {
          // Stack-owned warning: patch the matching stack's override refId.
          const nextStacks = computeRelinkPatchForStack(freshState.regionStacks, warning, asset);
          store.patch({ regionStacks: nextStacks });
        } else {
          // Region-owned warning: patch the matching region entry's override refId.
          const nextEntries = computeRelinkPatchForRegion(freshState.regionEntries, warning, asset);
          store.patch({ regionEntries: nextEntries });
        }

        closeDialog();
      };

      item.addEventListener('click', commitRelink);
      item.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          commitRelink();
        }
      });

      list.appendChild(item);
    }

    body.appendChild(list);
  }

  panel.appendChild(body);

  // ---------------------------------------------------------------------------
  // Footer - Cancel button
  // ---------------------------------------------------------------------------

  const footer = document.createElement('div');
  footer.className = 'relink-footer';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'relink-cancel btn';
  cancelBtn.textContent = t('dialog.relink.cancel');
  cancelBtn.addEventListener('click', closeDialog);
  footer.appendChild(cancelBtn);

  panel.appendChild(footer);

  // ---------------------------------------------------------------------------
  // Close / keyboard / backdrop
  // ---------------------------------------------------------------------------

  function closeDialog(): void {
    document.removeEventListener('keydown', handleKeydown);
    backdrop.remove();
  }

  function handleKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      closeDialog();
      return;
    }
    focusTrap(panel, ev);
  }

  // Backdrop click outside the panel closes.
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) {
      closeDialog();
    }
  });

  document.addEventListener('keydown', handleKeydown);

  // ---------------------------------------------------------------------------
  // Mount + focus
  // ---------------------------------------------------------------------------

  parent.appendChild(backdrop);

  // Focus the panel (or the first focusable child) after mounting.
  const firstFocusable = panel.querySelector<HTMLElement>(
    'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  (firstFocusable ?? panel).focus();
}
